import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CPSearch from "../components/CPSearch";
import AdCard from "../components/AdCard";
import { useAuth } from "../context/AuthContext";
import {
  MapPin,
  MessageCircle,
  Search,
  Tag,
  Sparkles,
  Smartphone,
  ShieldCheck,
  ArrowRight,
  Heart,
  Zap,
  Percent,
  Gift,
} from "lucide-react";
import { api } from "../lib/api";

// Shopper-focused imagery (people shopping + local commerce energy)
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/c2afb0df-2913-4bfa-9056-e084c30908bf/images/9aa9548337ac4a3dda3f9e8694e4749cd322346f976a12e03c86410cc7e92714.png";
const SHOPPING_IMGS = [
  // happy shoppers + market vibe
  "https://images.pexels.com/photos/5872361/pexels-photo-5872361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/16837824/pexels-photo-16837824.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/5650026/pexels-photo-5650026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/3962285/pexels-photo-3962285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function Landing() {
  const { user, loading } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [directories, setDirectories] = useState([]);

  useEffect(() => {
    api.get("/directory/featured?limit=8").then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get("/directory/list").then((r) => setDirectories(r.data || [])).catch(() => {});
  }, []);

  if (!loading && user) {
    const dashboardMap = {
      admin: "/admin",
      business: "/dashboard",
      customer: "/mi-cuenta",
      promoter: "/promotor",
    };
    return <Navigate to={dashboardMap[user.role] || "/"} replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO — Shopper focused */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy + CP */}
          <div className="lg:col-span-7 fade-in-up relative z-10">
            <div className="overline text-[hsl(var(--primary))] inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
              Tu colonia · ofertas todos los días
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.02] tracking-tight mt-4">
              Los mejores <span className="bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] bg-clip-text text-transparent">negocios</span> de tu colonia.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Ahora por cada compra en estos <strong>NEGOCIOS</strong> vas a recibir <strong>PUNTOS</strong> para cambiarlos por <strong>REGALOS</strong>. También recibe <strong>BOLETOS</strong> para rifas mensuales. Promociones reales, recompensas y precios especiales — agrupados por tu código postal.
            </p>

            {/* Trust strip */}
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Smartphone className="w-4 h-4 text-[#7C3AED]" /> Sin app que descargar</span>
              <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#7C3AED]" /> 100% gratis para clientes</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#7C3AED]" /> Negocios verificados</span>
            </div>

            {/* CP search */}
            <div id="visita-directorio" className="mt-8 max-w-xl">
              <div className="overline text-zinc-700 mb-2">Visita tu directorio local</div>
              <CPSearch size="lg" />
              <div className="mt-2 text-xs text-muted-foreground pl-2">
                Escribe tu código postal y descubre todas las promociones cerca de ti.
              </div>
            </div>
          </div>

          {/* Right: stacked shopper images collage */}
          <div className="lg:col-span-5 relative">
            <div className="relative grid grid-cols-2 grid-rows-2 gap-3 h-[540px]">
              <div className="row-span-2 rounded-3xl overflow-hidden border border-border bg-white shadow-xl">
                <img src={HERO_BG} alt="Comercios locales con ofertas" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden border border-border bg-white shadow">
                <img src={SHOPPING_IMGS[2]} alt="Persona comprando" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden border border-border bg-white shadow">
                <img src={SHOPPING_IMGS[1]} alt="Mercado local" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Floating discount stickers */}
            <div className="absolute -bottom-5 -left-4 hidden md:block bg-white rounded-2xl p-3 shadow-lg border border-border w-48">
              <div className="overline text-muted-foreground">Tendencia hoy</div>
              <div className="discount-stamp inline-block mt-1 px-2 py-1 rounded text-xs font-bold">2x1 EN TACOS</div>
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Zap className="w-3 h-3 text-[#7C3AED]" /> 248 vecinos lo vieron</div>
            </div>
            <div className="absolute -top-3 -right-2 hidden md:block bg-[#0E0E12] text-white rounded-full px-3 py-1.5 text-xs font-medium shadow-lg">
              <Percent className="w-3 h-3 inline mr-1" /> hasta 50% OFF
            </div>
          </div>
        </div>

        {/* background tints */}
        <div aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#7C3AED] opacity-[0.18] blur-3xl" />
        <div aria-hidden className="absolute top-40 -left-32 w-96 h-96 rounded-full bg-[#A78BFA] opacity-[0.18] blur-3xl" />
      </section>

      {/* VALUE PROPS for shoppers — bento style */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="overline text-[hsl(var(--primary))]">Por qué OFERTAS</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Cinco razones para usarlo todos los días.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 stagger">
          <Value
            variant="amber"
            big="puntos"
            bigLabel="Acumula y gana premios"
            icon={<Gift className="w-5 h-5" />}
            title="Recibe premios al acumular puntos"
            body="Cada compra en un comercio participante te da puntos en tu cuenta. Cuando llegas al tope, canjeas por un regalo: una pizza, un corte de pelo, un café, una hamburguesa — lo que cada comercio decidió premiar. Tus puntos se acumulan automáticamente al mostrar tu QR en caja."
          />
          <Value
            variant="purple"
            big="50%"
            bigLabel="Menos de lo que pagarías afuera"
            icon={<Percent className="w-5 h-5" />}
            title="Ahorra de verdad"
            body="Descuentos reales en comercios verificados. Nada de cupones que nunca aplican."
          />
          <Value
            variant="light"
            big="CP"
            bigLabel="Filtrado por código postal"
            icon={<MapPin className="w-5 h-5" />}
            title="Cerca de tu casa"
            body="Agrupamos los negocios por CP. Lo más cercano a tu colonia, primero."
          />
          <Value
            variant="dark"
            big="1 tap"
            bigLabel="Y ya estás chateando"
            icon={<MessageCircle className="w-5 h-5" />}
            title="Contacto directo"
            body="Toca el botón de WhatsApp y reclama tu oferta — sin filtros, sin intermediarios."
          />
          <Value
            variant="lavender"
            big="cada mes"
            bigLabel="Anuncios nuevos sin parar"
            icon={<Sparkles className="w-5 h-5" />}
            title="Promos frescas"
            body="Los negocios renuevan sus anuncios. Siempre hay algo nuevo que descubrir cerca de ti."
          />
        </div>
      </section>

      {/* RAFFLE teaser — placed right after the 5 value props to chain the excitement */}
      <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="raffle-teaser-landing">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-amber-200 via-amber-300 to-amber-500 text-amber-950 p-10 md:p-14">
          <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-white/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-72 h-72 rounded-full bg-[#7C3AED]/10 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="overline">RIFA MENSUAL · GRATIS PARA TI</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-2 leading-[1.05]">
                Compra en comercios OFERTAS<br />
                y <span className="text-[#5B21B6]">gana electrodomésticos</span> cada mes
              </h2>
              <p className="mt-4 text-base sm:text-lg max-w-xl">
                <strong>Por cada compra recibes un boleto digital.</strong> El último día de cada mes, OFERTAS México hace una rifa entre todos los boletos. Más compras = más boletos = más posibilidades de ganar.
              </p>
              <ul className="mt-5 space-y-2 text-base">
                <li className="flex items-start gap-2"><span className="text-[#5B21B6] font-bold mt-0.5">✓</span> Refrigeradores, pantallas, microondas y más.</li>
                <li className="flex items-start gap-2"><span className="text-[#5B21B6] font-bold mt-0.5">✓</span> Sin costo, sin trucos — el boleto se emite automáticamente.</li>
                <li className="flex items-start gap-2"><span className="text-[#5B21B6] font-bold mt-0.5">✓</span> Tus boletos viven en tu cuenta. Los ves en tiempo real.</li>
              </ul>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/registro-cliente"
                  className="inline-flex items-center gap-2 bg-[#5B21B6] text-white rounded-full px-6 py-3.5 font-semibold hover:bg-[#4C1D95] transition-colors"
                  data-testid="cta-raffle-customer"
                >
                  Crear mi cuenta y participar <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-3">
              <div className="bg-white/40 backdrop-blur border border-white/60 rounded-2xl p-5">
                <div className="text-[#5B21B6] text-4xl font-display">1</div>
                <div className="text-base font-medium mt-1.5">boleto por compra</div>
              </div>
              <div className="bg-white/40 backdrop-blur border border-white/60 rounded-2xl p-5">
                <div className="text-[#5B21B6] text-4xl font-display">12</div>
                <div className="text-base font-medium mt-1.5">rifas al año</div>
              </div>
              <div className="bg-white/40 backdrop-blur border border-white/60 rounded-2xl p-5 col-span-2">
                <div className="font-display text-2xl text-[#5B21B6]">Tus boletos están en tu cuenta</div>
                <div className="text-base mt-2">Cada mes se renuevan automáticamente.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED OFFERS */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="overline text-[hsl(var(--primary))]">Ofertas destacadas</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">Lo que está sonando hoy</h2>
            <p className="text-base sm:text-lg text-muted-foreground mt-2 max-w-xl">
              Una muestra de las promociones que ya están publicadas. Busca tu código postal para ver todas las de tu zona.
            </p>
          </div>
          <Link to="/directorios" className="text-sm font-medium text-[#7C3AED] hover:underline inline-flex items-center gap-1" data-testid="featured-see-all">
            Ver todos los directorios <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {featured.length === 0 ? (
          <EmptyFeatured />
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 stagger" data-testid="featured-grid">
            {featured.slice(0, 8).map((b, i) => (
              <AdCard key={b.id} business={b} ad={b.ad} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS for shoppers */}
      <section className="bg-[#FAFAFA] border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="overline text-[hsl(var(--primary))]">Cómo encontrar tus ofertas</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">Tres pasos para ahorrar.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12 stagger">
            <ShopperStep n="01" icon={<Search className="w-5 h-5" />} title="Escribe tu código postal" body="Sin registro, sin descargar nada. Solo tu CP y listo." />
            <ShopperStep n="02" icon={<Tag className="w-5 h-5" />} title="Explora las promociones" body="Mira los anuncios de los negocios que están en tu colonia con descuentos visibles." />
            <ShopperStep n="03" icon={<MessageCircle className="w-5 h-5" />} title="Visita los negocios de tu elección o contacta por WhatsApp" body="Ve directo al local que te interesa o toca el botón verde para escribirle al negocio y reclamar tu oferta." />
          </div>
        </div>
      </section>

      {/* COLLAGE / lifestyle */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
          {SHOPPING_IMGS.map((src, i) => (
            <div key={src} className="aspect-[4/3] rounded-xl overflow-hidden border border-border group relative">
              <img src={src} alt={`comprador-${i}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12]/60 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 text-white text-xs font-medium">
                {["Vecinos felices", "Mercado local", "Compradores", "Tu colonia"][i]}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DIRECTORIES preview */}
      {directories.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="overline text-muted-foreground">Directorios activos</div>
              <h3 className="font-display text-3xl tracking-tight">Encuentra el tuyo</h3>
            </div>
            <Link to="/directorios" className="text-sm font-medium text-[#7C3AED] hover:underline">Ver todos →</Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {directories.slice(0, 8).map((d) => (
              <Link
                key={d.postal_code}
                to={`/directorio/${d.postal_code}`}
                className="group bg-white border border-border rounded-xl p-4 hover:border-[#7C3AED] transition-colors"
                data-testid={`dir-preview-${d.postal_code}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-muted-foreground">CP {d.postal_code}</div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#7C3AED]" />
                </div>
                {d.name ? (
                  <div className="font-display text-base mt-0.5 text-[#5B21B6] leading-tight">{d.name}</div>
                ) : (
                  <div className="text-sm text-muted-foreground mt-1">{d.colonia || "Directorio local"}</div>
                )}
                <div className="overline mt-2 text-[#7C3AED]">{d.count} comercios</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SECONDARY CTA: affiliate businesses */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl gradient-dark text-white p-10 md:p-14">
          <div aria-hidden className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#7C3AED] opacity-30 blur-3xl" />
          <div className="relative grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="overline text-[#C4B5FD]">¿Tienes un negocio?</div>
              <h3 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">
                Afilia tu negocio y aparece <span className="text-[#A78BFA]">en el directorio de tu colonia</span>.
              </h3>
              <p className="text-base sm:text-lg text-zinc-300 mt-3">
                Por $199 MXN al mes, la IA diseña tu anuncio y miles de vecinos lo verán cada vez que busquen su código postal.
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <Link
                to="/afilia-tu-negocio"
                className="inline-flex items-center gap-2 bg-white text-[#0E0E12] rounded-full px-6 py-3 font-medium hover:bg-zinc-100 transition-colors"
                data-testid="cta-affiliate"
              >
                Afiliar mi negocio <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROMOTORES section */}
      <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="promoters-section">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#7C3AED] via-[#6D28D9] to-[#4C1D95] text-white p-10 md:p-14">
          <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-3">
              <div className="overline text-amber-300">PROMOTORES · GANA DINERO CON OFERTAS</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-3 leading-[1.05]">
                Gana dinero cada mes<br />
                por cada comercio que afilies
              </h2>
              <p className="text-white/85 mt-4 max-w-xl text-base sm:text-lg">
                Conviértete en promotor OFERTAS, recomienda nuestro directorio a comercios locales y cobra una comisión recurrente cada vez que paguen su cuota mensual de $199. Sin tope, sin letra chiquita.
              </p>
              <ul className="mt-6 space-y-2 text-base text-white/90">
                <li className="flex items-start gap-2"><span className="text-amber-300 font-bold mt-0.5">✓</span> Comisión mensual recurrente — mientras el comercio siga pagando, tú sigues cobrando.</li>
                <li className="flex items-start gap-2"><span className="text-amber-300 font-bold mt-0.5">✓</span> Panel propio con reportes de tus comercios y ganancias.</li>
                <li className="flex items-start gap-2"><span className="text-amber-300 font-bold mt-0.5">✓</span> Sin inversión, sin inventario. Solo tu red de contactos.</li>
              </ul>
              <div className="mt-7">
                <Link
                  to="/login-promotor"
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white rounded-full px-6 py-3.5 font-medium hover:bg-white/20 transition-colors"
                  data-testid="cta-promoter-login"
                >
                  Ya soy promotor · Iniciar sesión
                </Link>
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 gap-3">
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5">
                <div className="text-amber-300 text-4xl font-display">∞</div>
                <div className="text-base text-white/85 mt-1.5">comercios sin tope</div>
              </div>
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5">
                <div className="font-display text-2xl">Cada vez que tu comercio paga…</div>
                <div className="text-base text-white/85 mt-2">…tu comisión aparece automáticamente en tu panel. Te la pagamos al final del mes.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const VALUE_STYLES = {
  amber: {
    card: "bg-gradient-to-br from-amber-300 via-amber-400 to-amber-500 text-amber-950 border-transparent shadow-[0_20px_40px_-18px_rgba(245,158,11,0.55)]",
    iconBox: "bg-white/30 backdrop-blur text-amber-950 ring-1 ring-white/40",
    title: "text-amber-950",
    body: "text-amber-900",
    bigBg: "text-white/30",
    overline: "text-[#5B21B6]",
    blob: "bg-white/20",
  },
  purple: {
    card: "bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] text-white border-transparent shadow-[0_20px_40px_-18px_rgba(124,58,237,0.55)]",
    iconBox: "bg-white/15 backdrop-blur text-white ring-1 ring-white/25",
    title: "text-white",
    body: "text-purple-100/90",
    bigBg: "text-white/15",
    overline: "text-purple-200",
    blob: "bg-white/10",
  },
  light: {
    card: "bg-white border-[#C4B5FD] hover:border-[#7C3AED] hover:shadow-xl",
    iconBox: "bg-gradient-to-br from-[#F5F3FF] to-[#EDE9FE] text-[#7C3AED] ring-1 ring-[#DDD6FE]",
    title: "text-[#0E0E12]",
    body: "text-muted-foreground",
    bigBg: "text-[#7C3AED]/10",
    overline: "text-[#7C3AED]",
    blob: "bg-[#EDE9FE]",
  },
  dark: {
    card: "bg-gradient-to-br from-zinc-100 to-[#EDE9FE] border-[#C4B5FD] hover:border-[#7C3AED] hover:shadow-xl text-[#0E0E12]",
    iconBox: "bg-white text-[#7C3AED] ring-1 ring-[#DDD6FE]",
    title: "text-[#0E0E12]",
    body: "text-zinc-600",
    bigBg: "text-[#7C3AED]/10",
    overline: "text-[#5B21B6]",
    blob: "bg-[#DDD6FE]",
  },
  lavender: {
    card: "bg-[#F5F3FF] border-[#C4B5FD] hover:shadow-xl hover:border-[#7C3AED]",
    iconBox: "bg-white text-[#7C3AED] ring-1 ring-[#DDD6FE]",
    title: "text-[#0E0E12]",
    body: "text-zinc-700",
    bigBg: "text-[#7C3AED]/15",
    overline: "text-[#5B21B6]",
    blob: "bg-white/60",
  },
};

const Value = ({ variant = "light", big, bigLabel, icon, title, body }) => {
  const s = VALUE_STYLES[variant];
  return (
    <div className={`group relative overflow-hidden border rounded-3xl p-7 min-h-[260px] flex flex-col transition-all duration-300 hover:-translate-y-1.5 ${s.card}`}>
      {/* decorative blob */}
      <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-2xl opacity-70 ${s.blob} group-hover:opacity-100 transition-opacity`} />
      {/* huge background label */}
      <div className={`absolute -bottom-3 right-3 font-display text-[5.5rem] leading-none tracking-tighter select-none pointer-events-none ${s.bigBg}`}>
        {big}
      </div>

      <div className={`relative w-12 h-12 rounded-2xl grid place-items-center ${s.iconBox} shadow-sm group-hover:rotate-[-6deg] transition-transform duration-300`}>
        {icon}
      </div>

      <div className="relative mt-6 flex-1">
        <div className={`font-display text-2xl tracking-tight leading-tight ${s.title}`}>{title}</div>
        <p className={`text-base mt-2 leading-relaxed ${s.body}`}>{body}</p>
      </div>

      <div className={`relative overline mt-5 text-[10px] ${s.overline}`}>{bigLabel}</div>
    </div>
  );
};

const ShopperStep = ({ n, icon, title, body }) => (
  <div className="bg-white border border-border rounded-2xl p-6 relative">
    <div className="overline text-muted-foreground">{n}</div>
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] text-white grid place-items-center mt-3">
      {icon}
    </div>
    <div className="font-display text-xl mt-4 tracking-tight">{title}</div>
    <p className="text-sm text-muted-foreground mt-2">{body}</p>
  </div>
);

const EmptyFeatured = () => (
  <div className="bg-white border border-dashed border-border rounded-2xl p-10 text-center">
    <div className="font-display text-2xl">Pronto verás aquí las ofertas más buscadas</div>
    <p className="text-muted-foreground mt-2">Mientras tanto, escribe tu código postal arriba para ver lo que ya hay cerca de ti.</p>
  </div>
);
